import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { Zap, Plus, Play, CheckCircle2, ArrowRight, Settings2, Sliders, Mail, UserPlus, Tag } from "lucide-react";
import { toast } from "sonner";

export default function WorkflowBuilderPage() {
  const queryClient = useQueryClient();
  const [ruleName, setRuleName] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("FORM_SUBMITTED");
  const [actionType, setActionType] = useState("SEND_EMAIL");

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["workflow-rules"],
    queryFn: async () => {
      const res = await apiClient.get("/workflows/rules");
      return res.data;
    },
  });

  const createRuleMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/workflows/rules", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["workflow-rules"]);
      toast.success("Trigger-action workflow rule created!");
      setRuleName("");
    },
  });

  const testTriggerMutation = useMutation({
    mutationFn: async (evt) => {
      const res = await apiClient.post("/workflows/test-trigger", { event: evt });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Workflow Engine Executed ${data.executedCount} Rule(s)!`);
      queryClient.invalidateQueries(["workflow-rules"]);
    },
  });

  const handleCreateRule = (e) => {
    e.preventDefault();
    if (!ruleName.trim()) return;
    createRuleMutation.mutate({
      name: ruleName,
      trigger_event: triggerEvent,
      actions: [{ action_type: actionType, config: { target: "admin@miracle.org" } }],
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-7 h-7 text-indigo-600" />
            Automated Form Builder & Workflow Engine
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure automated rules: <code>Trigger &rarr; Condition &rarr; Action</code> dispatches.
          </p>
        </div>
        <button
          onClick={() => testTriggerMutation.mutate("FORM_SUBMITTED")}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs rounded-xl shadow-sm transition-all"
        >
          <Play className="w-4 h-4 fill-white" />
          Test Run Form Submission Trigger
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workflow Rule Creator */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Sliders className="w-4 h-4 text-indigo-600" />
            Configure New Automation Rule
          </h2>

          <form onSubmit={handleCreateRule} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-700">Rule Name</label>
              <input
                required
                type="text"
                placeholder="e.g. Volunteer Registration Welcome Email"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                1. Trigger Event (WHEN)
              </label>
              <select
                value={triggerEvent}
                onChange={(e) => setTriggerEvent(e.target.value)}
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-xs font-semibold bg-indigo-50/50 text-indigo-900"
              >
                <option value="FORM_SUBMITTED">Form Submitted (Aid or Volunteer Intake)</option>
                <option value="CONTACT_CREATED">New Contact Profile Created</option>
                <option value="DONATION_RECEIVED">Donation Received</option>
                <option value="VOLUNTEER_CHECKED_IN">Volunteer Checked In</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                2. Automated Action (THEN DO)
              </label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-xs font-semibold bg-emerald-50/50 text-emerald-900"
              >
                <option value="SEND_EMAIL">Send Email Broadcast via SendGrid</option>
                <option value="SEND_SMS">Send SMS Notification via Twilio</option>
                <option value="CREATE_CONTACT">Auto-Create Unified CRM Contact</option>
                <option value="ADD_TAG">Assign Identity Tag</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={createRuleMutation.isPending}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Save Workflow Rule
            </button>
          </form>
        </div>

        {/* Existing Rules List */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 flex items-center justify-between">
            <span>Active Trigger-Action Automation Engine ({rules.length})</span>
          </h2>

          {isLoading ? (
            <div className="p-8 text-center text-xs text-slate-500">Loading rules...</div>
          ) : rules.length === 0 ? (
            <div className="p-12 text-center text-xs text-slate-400">
              No automation rules configured yet. Use the form on the left to set up a rule.
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule._id} className="p-4 rounded-xl border border-slate-200/80 bg-slate-50/60 flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="font-bold text-slate-900 text-sm">{rule.name}</h3>
                    <div className="flex items-center gap-2 text-xs font-mono">
                      <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold">
                        {rule.trigger_event}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                        {rule.actions?.[0]?.action_type || "ACTION"}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-slate-500 block">
                      Executions: {rule.execution_count}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
